/** Versioned, byte-pinned curated media contract shared by server and player.
 * No browser APIs, remote URLs, or implicit substitutions. */
export const CURATED_IMPACT_V1 = Object.freeze({
  variantId: 'platform-impact-v1', source: 'curated-impact-v1' as const,
  contentSha256: '30e7585345e85bdcd6d9c5f51ccc1e485d1aeeecb21b6dcd638c895ecece5b7c',
  mime: 'audio/wav', bytes: 7724, durationMs: 160, sampleRate: 24000, channels: 1,
  provenance: 'Project-authored integer synthesis: packages/audio-web/src/pcmSfx.ts, v1',
});
export const SFX_FAMILIES = ['arcade', 'soft'] as const;
export type SfxFamily = (typeof SFX_FAMILIES)[number];
export type AudioSelection = 'none' | {
  variantId: string;
  source: 'curated-impact-v1' | 'uploaded-audio-v1' | 'bank-sfx-v1';
  contentSha256: string;
};
/** Historical name retained for v1 callers. V2 adds uploaded samples and music;
 * V3 adds a version-owned bank family and exact bank-entry overrides.
 * Selections contain identities only, never client-supplied playback URLs. */
export interface CuratedSfxSpec {
  schemaVersion: 1 | 2 | 3;
  cues: Record<string, AudioSelection>;
  music?: AudioSelection;
  family?: SfxFamily;
}
export function audioSelections(spec: CuratedSfxSpec): AudioSelection[] {
  return [...Object.values(spec.cues), ...(spec.music === undefined ? [] : [spec.music])];
}
export function validateCuratedSfx(input: unknown): CuratedSfxSpec {
  const value = input as CuratedSfxSpec;
  if (!value || ![1, 2, 3].includes(value.schemaVersion) || !value.cues || typeof value.cues !== 'object' ||
    Array.isArray(value.cues) || Object.keys(value.cues).length > 32 ||
    Object.keys(value).some(k => !['schemaVersion', 'cues', ...(value.schemaVersion >= 2 ? ['music'] : []), ...(value.schemaVersion === 3 ? ['family'] : [])].includes(k)) ||
    (value.schemaVersion === 3 && value.family !== undefined && !(SFX_FAMILIES as readonly unknown[]).includes(value.family))) throw new Error('Invalid curated SFX bindings');
  const cues: CuratedSfxSpec['cues'] = {};
  const read = (binding: AudioSelection, music = false): AudioSelection => {
    if (binding === 'none') return binding;
    if (!binding || typeof binding !== 'object' || Array.isArray(binding) || Object.keys(binding).length !== 3) throw new Error('Invalid audio selection');
    if (value.schemaVersion === 2 && binding.source === 'uploaded-audio-v1' &&
      typeof binding.variantId === 'string' && typeof binding.contentSha256 === 'string' &&
      !['__proto__', 'constructor', 'prototype'].includes(binding.variantId) &&
      /^[A-Za-z0-9][A-Za-z0-9_-]{0,99}$/.test(binding.variantId) && /^[a-f0-9]{64}$/.test(binding.contentSha256)) {
      return { variantId: binding.variantId, source: binding.source, contentSha256: binding.contentSha256 };
    }
    if (value.schemaVersion === 3 && binding.source === 'uploaded-audio-v1' &&
      typeof binding.variantId === 'string' && typeof binding.contentSha256 === 'string' &&
      !['__proto__', 'constructor', 'prototype'].includes(binding.variantId) &&
      /^[A-Za-z0-9][A-Za-z0-9_-]{0,99}$/.test(binding.variantId) && /^[a-f0-9]{64}$/.test(binding.contentSha256)) {
      return { variantId: binding.variantId, source: binding.source, contentSha256: binding.contentSha256 };
    }
    if (!music && value.schemaVersion === 3 && binding.source === 'bank-sfx-v1' &&
      typeof binding.variantId === 'string' && /^[a-z0-9_-]+\.(arcade|soft)\.v[12]$/.test(binding.variantId) &&
      typeof binding.contentSha256 === 'string' && /^[a-f0-9]{64}$/.test(binding.contentSha256)) {
      return { variantId: binding.variantId, source: binding.source, contentSha256: binding.contentSha256 };
    }
    if (music || binding.variantId !== CURATED_IMPACT_V1.variantId || binding.source !== CURATED_IMPACT_V1.source ||
      binding.contentSha256 !== CURATED_IMPACT_V1.contentSha256) throw new Error('Unknown or unverified SFX selection');
    return { variantId: binding.variantId, source: binding.source, contentSha256: binding.contentSha256 };
  };
  for (const [cue, binding] of Object.entries(value.cues).sort(([a], [b]) => a.localeCompare(b))) {
    if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(cue) || ['__proto__', 'constructor', 'prototype'].includes(cue)) throw new Error('Invalid SFX cue');
    cues[cue] = read(binding);
  }
  return { schemaVersion: value.schemaVersion, cues,
    ...(value.music === undefined ? {} : { music: read(value.music, true) }),
    ...(value.schemaVersion === 3 && value.family !== undefined ? { family: value.family } : {}) };
}
